import { Fragment, useCallback, useState } from 'react';
import type { MouseEvent } from 'react';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';

import { IconEnum } from '@e-socna/data-layer';

import { useNotificationsContext } from '../../../contexts/NotificationsContext/hooks/useNotificationsContext.hooks';
import { iconToMuiIcon } from '../../../utils/icon-to-mui-icon.util';

export const Notifications = () => {
  const { getUnreadNotifications, markAllNotificationsAsRead } =
    useNotificationsContext();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = useCallback(
    (event: Readonly<MouseEvent<HTMLButtonElement>>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    markAllNotificationsAsRead();
    setAnchorEl(null);
  }, [markAllNotificationsAsRead]);

  const open = Boolean(anchorEl);
  const id = open ? 'notifications-popover' : undefined;

  const notifications = getUnreadNotifications();

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Tooltip title="Afficher les notifications">
        <IconButton
          aria-label="Afficher les notifications"
          onClick={handleClick}
          sx={{ mr: 1 }}
        >
          <Badge badgeContent={notifications.length} color="secondary">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <List
          sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}
        >
          {notifications.length ? (
            notifications.map((notification, idx, arr) => (
              <Fragment key={notification._id}>
                <ListItem>
                  {!!notification.chip && (
                    <ListItemIcon>
                      {iconToMuiIcon(notification.chip.icon)}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={notification.primaryText}
                    secondary={notification.secondaryText}
                  />
                </ListItem>
                {idx < arr.length - 1 && (
                  <Divider component="li" variant="inset" />
                )}
              </Fragment>
            ))
          ) : (
            <ListItem key="empty-notification-list">
              <ListItemIcon>
                {iconToMuiIcon(IconEnum.SentimentSatisfiedAlt)}
              </ListItemIcon>
              <ListItemText primary="Vous n'avez pas de notifications !" />
            </ListItem>
          )}
        </List>
      </Popover>
    </Box>
  );
};
